import type { HTMLProps, ReactElement } from 'react';
import cx from 'clsx';
import IconActivateFilledSmall from '@repo/monochrome-icons/icon-activate-filled-small';
import IconClockFilledSmall from '@repo/monochrome-icons/icon-clock-filled-small';
import IconEinvoicingFilledSmall from '@repo/monochrome-icons/icon-einvoicing-filled-small';
import IconNewFilledSmall from '@repo/monochrome-icons/icon-new-filled-small';
import IconNotEligibleFilledSmall from '@repo/monochrome-icons/icon-not-eligible-filled-small';
import IconPromoFilledSmall from '@repo/monochrome-icons/icon-promo-filled-small';
import IconSaveFilledSmall from '@repo/monochrome-icons/icon-save-filled-small';
import IconTryFilledSmall from '@repo/monochrome-icons/icon-try-filled-small';
import IconUpgradeFilledSmall from '@repo/monochrome-icons/icon-upgrade-filled-small';
import styles from './badge.module.css';

type ValueOf<T> = T[keyof T];

export const BADGE_TYPE = {
  ACTIVATE: 'activate',
  NEW_FEATURE: 'newFeature',
  NOT_AVAILABLE: 'notAvailable',
  LIMITED_TIME: 'limitedTime',
  PROMO: 'promo',
  SAVE: 'save',
  TRY_FOR_FREE: 'tryForFree',
  UNION: 'union',
  UPGRADE: 'upgrade',
} as const;

export type BadgeType = ValueOf<typeof BADGE_TYPE>;

export interface BadgeProps extends HTMLProps<HTMLSpanElement> {
  type: BadgeType;
  text?: string;
  className?: string;
}
const BADGE_STYLE = {
  [BADGE_TYPE.ACTIVATE]: {
    IconComponent: IconActivateFilledSmall,
    backgroundClass: styles.bgActivate,
    alt: 'activate',
  },
  [BADGE_TYPE.TRY_FOR_FREE]: {
    IconComponent: IconTryFilledSmall,
    backgroundClass: styles.bgFreeTrial,
    alt: 'try for free',
  },
  [BADGE_TYPE.UPGRADE]: {
    IconComponent: IconUpgradeFilledSmall,
    backgroundClass: styles.bgUpgrade,
    alt: 'upgrade',
  },
  [BADGE_TYPE.SAVE]: {
    IconComponent: IconSaveFilledSmall,
    backgroundClass: styles.bgSave,
    alt: 'save',
  },
  [BADGE_TYPE.LIMITED_TIME]: {
    IconComponent: IconClockFilledSmall,
    backgroundClass: styles.bgUpgrade,
    alt: 'limitedTime',
  },
  [BADGE_TYPE.PROMO]: {
    IconComponent: IconPromoFilledSmall,
    backgroundClass: styles.bgPromo,
    alt: 'promo',
  },
  [BADGE_TYPE.NOT_AVAILABLE]: {
    IconComponent: IconNotEligibleFilledSmall,
    backgroundClass: styles.bgSecondary,
    alt: 'notAvailable',
  },
  [BADGE_TYPE.NEW_FEATURE]: {
    IconComponent: IconNewFilledSmall,
    backgroundClass: styles.bgNew,
    alt: 'new feature',
  },
  [BADGE_TYPE.UNION]: {
    IconComponent: IconEinvoicingFilledSmall,
    backgroundClass: styles.bgSecondary,
    alt: 'union',
  },
} as const;

export function Badge({ type, text, className, ...props }: BadgeProps): ReactElement {
  const { IconComponent, backgroundClass, alt } = BADGE_STYLE[type];
  const hasText = Boolean(text?.length);

  return (
    <span
      className={cx(
        'caption',
        styles.badge,
        backgroundClass,
        hasText && styles.withText,
        className
      )}
      data-test-badge={type}
      {...props}
    >
      <span className={styles.iconContainer}>
        <IconComponent
          aria-label={alt}
          data-test-badge-icon={IconComponent.name}
          height={12}
          role="img"
          width={12}
        />
      </span>
      {hasText && (
        <span className={styles.textContainer}>
          <span>{text}</span>
        </span>
      )}
    </span>
  );
}
